import { Cdr } from '@reliance/types/models/Cdr';
import { Inmate } from '@reliance/types/models/Inmate';
import React, { useCallback, useEffect, useMemo } from 'react';
import CdrTable, { CDR_TABLE_SERVER_COLUMNS } from '../../cdr/Table';
import { useCdrsByInmateId } from '../../hooks/Cdr';
import { useTabLazyLoad } from '../../hooks/useTabLazyLoad';
import { useNotifyTabLoading } from '../../hooks/useTabLoadingReporter';
import { useServerTableFilterState } from '../../table/useServerTableFilterState';

type InmateCdrsProps = {
  inmate: Inmate;
  tabSelected?: boolean;
  refreshTrigger?: number;
};

const INMATE_CDR_COLUMNS = CDR_TABLE_SERVER_COLUMNS.filter(
  (column) => column.id !== 'facility' && column.id !== 'inmate'
);

const InmateCdrs: React.FC<InmateCdrsProps> = (props) => {
  const { inmate, tabSelected, refreshTrigger } = props;

  const baseRecord = useMemo(
    () => ({ inmateId: inmate.id, orderBy: '-setupDate' }) as Record<string, unknown>,
    [inmate.id]
  );
  const tableState = useServerTableFilterState(INMATE_CDR_COLUMNS, baseRecord, {
    initialPagination: { pageIndex: 0, pageSize: 500 },
    initialSorting: [{ id: 'setupDate', desc: true }],
  });

  const filters = useMemo(() => {
    const merged = tableState.mergedRecord;
    const callTypes = merged.callTypes as string | undefined;
    const sourceIds = merged.sourceIds as string | undefined;
    const destinations = merged.destinations as string | undefined;
    return {
      inmateId: inmate.id,
      page: merged.page as number,
      perPage: merged.perPage as number,
      orderBy: (merged.orderBy as string | undefined) ?? '-setupDate',
      ...(callTypes != null && callTypes !== '' && { callTypes }),
      ...(sourceIds != null && sourceIds !== '' && { sourceIds }),
      ...(destinations != null && destinations !== '' && { destinations }),
    };
  }, [inmate.id, tableState.mergedRecord]);

  const cdrsResult = useCdrsByInmateId(null, filters);

  useTabLazyLoad({
    key: inmate.id,
    tabSelected,
    refreshTrigger,
    execute: useCallback(() => {
      if (inmate.id) {
        cdrsResult.execute(inmate.id);
      }
    }, [cdrsResult, inmate.id]),
  });

  useEffect(() => {
    if (tabSelected && inmate.id) {
      cdrsResult.execute(inmate.id);
    }
  }, [tabSelected, inmate.id, tableState.mergedRecord]);

  useNotifyTabLoading('cdrs', cdrsResult.loading);

  const cdrData: Cdr[] = cdrsResult.result?.data ?? [];
  const total = cdrsResult.result?.total ?? 0;
  const perPage = cdrsResult.result?.perPage ?? tableState.pagination.pageSize;
  const totalPages = Math.ceil(total / perPage) || 1;
  const page = cdrsResult.result?.page ?? tableState.pagination.pageIndex + 1;

  return (
    <div className="jarviswidget jarviswidget-color-blueDark">
      <div>
        <CdrTable
          rows={cdrData}
          hiddenColumns={['facility', 'inmate']}
          enableColumnFilters
          page={page}
          totalPages={totalPages}
          total={total}
          perPage={perPage}
          serverPagination={tableState.serverPagination}
          onServerPaginationChange={tableState.onServerPaginationChange}
          serverSorting={tableState.serverSorting}
          onServerSortingChange={tableState.onServerSortingChange}
          serverColumnFilters={tableState.serverColumnFilters}
          onServerColumnFiltersChange={tableState.onServerColumnFiltersChange}
          paginationMode="server"
          loading={cdrsResult.loading}
        />
      </div>
    </div>
  );
};

export default InmateCdrs;
